import React, { useEffect, useState } from "react";
import Heading from "../../../component/Heading/Heading";
import TableComponent from "../../../component/Table/TableComponent";
import CommonModal from "../../../component/model/ModelComponent";
import InputComponent from "../../../component/input/Input";
import { addUserData } from "../../../content/content";
import CommonButton from "../../../component/button/Button";
import { Box, TableCell, TableRow, Typography } from "@mui/material";
import {
  addCustomerUser,
  deleteCustomerUser,
  getSingleCustomerUser,
  getCustomerUser,
  resendCustomerEmail,
  updateCustomerUser,
  toggleCustomerStatus,
} from "../../../services/customer-services/customerUserService";
import Toast from "../../../component/toastify/Index";
import editIcon from "../../../assets/icons/Edit.svg";
import deleteIcon from "../../../assets/icons/trash.svg";
import sendEmailIcon from "../../../assets/icons/send-mail.svg";
import { useTranslation } from "react-i18next";
import { getBillingStatus } from "../../../services/customer-services/customerBillingStatus";
import { jwtDecode } from "jwt-decode";
import CommonGradientSwitch from "../../../component/switch/CommonSwitch";
import "../../../styles/pages/customer/user.scss";

const columns = [
  { field: "firstName", headerName: "First Name", translationKey: 'common_label_first_name' },
  { field: "lastName", headerName: "Last Name", translationKey: 'common_label_last_name' },
  { field: "email", headerName: "Email", translationKey: 'common_label_email' },
  { field: "mobileNumber", headerName: "Mobile Number", translationKey: 'common_label_mobile_number' },
  { field: "status", headerName: "Status", translationKey: 'client_list_status' },
  { field: "action", headerName: "Action", translationKey: 'client_list_action' },
];

const CustomerUsers = () => {
  const { t } = useTranslation();
  const [user, setUser] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState({});
  const [editingUserId, setEditingUserId] = useState(null);
  const [errors, setErrors] = useState({});
  const [pageNo, setPageNo] = useState(1);
  const [perPage, setPerPage] = useState(25);
  const [totalRecords, setTotalRecords] = useState(0);
  const [billingStatus, setBillingStatus] = useState(null);
  const [userId, setUserId] = useState('')

  useEffect(() => {
    const decodedToken = jwtDecode(
      JSON.parse(localStorage.getItem("authToken"))
    );
    if (Object?.keys(decodedToken ?? {})?.length !== 0) {
      setUserId(decodedToken._id)
    }
    else {
      setUserId('')
    }
  }, [])


  useEffect(() => {
    getBillingStatus()
      .then((res) => {
        setBillingStatus(res);
      })
      .catch((err) => console.error(err))
  }, []);

  const fetchUsers = async () => {
    const response = await getCustomerUser({ pageNo, perPage });
    setUser(response?.userList?.userList);
    setTotalRecords(response?.userList?.totalRecords);
  };

  useEffect(() => {
    if (billingStatus?.isPlanActive) {
      fetchUsers();
    }
  }, [pageNo, perPage, billingStatus]);

  const validate = () => {
    const newErrors = {};
    let valid = true;

    addUserData.forEach((item) => {
      const value = formData[item.name]?.trim();
      if (!value) {
        newErrors[item.name] = `${t(item.translationKey)} ${t("common_is_required")}`;
        valid = false;
      } else if (item.name === "email" && !/^\S+@\S+\.\S+$/.test(value)) {
        newErrors[item.name] = `${t('common_is_valid_email')}`;
        valid = false;
      } else if (
        item.name === "mobileNumber" &&
        formData[item.name].length >= 20
      ) {
        newErrors[item.name] = `${t('common_is_valid_mobile_number')}`;
        valid = false;
      }
    });

    setErrors(newErrors);
    return valid;
  };

  const handleAdd = () => {
    setIsModalOpen(true);
    setFormData({});
    setEditingUserId(null);
    setErrors({});
  };

  const handleEdit = async (id) => {
    const response = await getSingleCustomerUser(id);
    setFormData(response.userDetail);
    setEditingUserId(id);
    setIsModalOpen(true);
    setErrors({});
  };

  const handleDelete = async (id) => {
    const response = await deleteCustomerUser(id);
    if (response?.isSuccess) {
      Toast(response?.message, "success");
      fetchUsers();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    if (editingUserId) {
      delete formData._id;
      const response = await updateCustomerUser(editingUserId, formData);
      if (response?.isSuccess) {
        if (response?.isSuccess) {
          Toast(response?.message, "success");
        }
      }
    } else {
      const response = await addCustomerUser(formData);
      if (response?.isSuccess) {
        Toast(response?.message, "success");
      }
    }
    setIsModalOpen(false);
    fetchUsers();
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    setErrors({ ...errors, [name]: "" });
  };

  const handleResendInvitation = async (email) => {
    const response = await resendCustomerEmail(email);
    if (response?.isSuccess) {
      Toast(response?.message, "success");
    }
  };

  const handleToggleCustomerStatus = async (id, status) => {
    const response = await toggleCustomerStatus(id, status);
    if (response.isSuccess) {
      Toast(response?.message, "success");
      fetchUsers();
    }
  };

  return (
    <div className="users">
      <CommonModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={editingUserId ? t('user_edit_user') : t('user_add_user')}
        className="model-title"
      >
        <Typography
          sx={{
            border: "1px solid #E3E3E3",
            padding: "20px",
            borderRadius: "10px",
          }}
        >
          <form onSubmit={handleSubmit}>
            {addUserData.map((item) => (
              <InputComponent
                key={item?.name}
                borderColor="E3E3E3"
                border="2px solid #E3E3E3"
                padding="9px 12px"
                type={item.type}
                placeholder={t(item.placeholderTranslationKey)}
                label={t(item.translationKey)}
                className="user-input"
                name={item?.name}
                value={formData[item?.name] || ""}
                error={!!errors[item.name]}
                helperText={errors[item.name]}
                onChange={handleChange}
              />
            ))}
            <Typography className="model-action-btn">
              {!editingUserId && (
                <CommonButton
                  text={t("user_clear_all")}
                  className="invitation-btn"
                  bgColor="linear-gradient(90deg, #E14A4A 0%, #FF4848 100%);"
                  onClick={() => setFormData({})}
                />
              )}
              <CommonButton
                type="submit"
                text={`${editingUserId ? t('user_update') : t("user_send_invitation")}`}
                className="add-user-btn"
              />
            </Typography>
          </form>
        </Typography>
      </CommonModal>

      <div className="user-heading flex items-center justify-between">
        <Heading text={t('module_user_management')} className="user-heading" />
        <CommonButton
          text={t("user_add_user")}
          onClick={handleAdd}
          className="add-user-btn"
        />
      </div>
      <div>
        <TableComponent
          data={user}
          setPageNo={setPageNo}
          pageNo={pageNo}
          perPage={perPage}
          setPerPage={setPerPage}
          totalRecords={totalRecords}
          columns={columns}
          className={'table-max-height'}
        >
          {user?.map((row, rowIndex) => (
            <TableRow key={rowIndex}>
              <TableCell>{row.firstName}</TableCell>
              <TableCell>{row.lastName}</TableCell>
              <TableCell>{row.email}</TableCell>
              <TableCell>{row.mobileNumber}</TableCell>
              <TableCell
                className={row.isDelete ? 'inactive-text' : 'active-text'}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: "center",
                    gap: 1,
                    whiteSpace: 'nowrap'
                  }}
                >
                  {row.isDelete
                    ? t('user_in_active')
                    : row.status
                      ? t('user_active')
                      : t('user_waiting_for_approval')
                  }
                  {
                    (row.isVerifyEmail && row?._id !== userId) && (
                      <CommonGradientSwitch
                        checked={row.status}
                        onChange={(event) => handleToggleCustomerStatus(row._id, event.target.checked)}
                      />
                    )
                  }
                </Box>

              </TableCell>
              <TableCell>
                <Typography
                  className="action"
                  classes="flex"
                  alignItems={"center"}
                  display={"flex"}
                >
                  {!row.status && !row.isDelete && (
                    <span onClick={() => handleResendInvitation(row.email)}>
                      <img src={sendEmailIcon} height={30} alt="send email icon" className="edit-icon" />
                    </span>
                  )}
                  <span onClick={() => handleEdit(row._id)}>
                    <img src={editIcon} alt="edit icon" className="edit-icon" />
                  </span>
                  <span onClick={() => handleDelete(row._id)}>
                    <img src={deleteIcon} alt="delete icon" />
                  </span>
                </Typography>
              </TableCell>
            </TableRow>
          ))}
        </TableComponent>
      </div>
    </div>
  );
};

export default CustomerUsers;
